import React, { useState, useEffect, useCallback, useRef } from 'react';
import { smeService, editorService, articleService, getApiErrorMessage, parseFetchErrorMessage } from '../services/api';
import { expireStoredSession, getToken } from '../services/auth';
import {
  FileText,
  Clock,
  Sparkles,
  Info,
  ChevronDown,
  ChevronUp,
  Check,
  Search,
  BookOpen,
  ArrowRight,
  Layout,
  Edit3,
  Copy,
  PenTool,
  Ship
} from 'lucide-react';
import './ArticleGenerator.css';

const MARKDOWN_LINK_GLOBAL = /\[([^\]]+)\]\(([^\s)]+)\)/gi;
const DEFAULT_EDITOR_NAME = 'Encye Editor (Default)';
const DEFAULT_EDITOR_API_NAME = 'Encye Editor';

const ARTICLE_LENGTH_OPTIONS = [
  { value: 'short', label: 'Short article (400 - 500 words)' },
  { value: 'long', label: 'Long article (more than 500 words)' },
];
const ENGLISH_STYLE_OPTIONS = [
  { value: 'us', label: 'US English' },
  { value: 'uk', label: 'UK English' },
];
const ENGLISH_DIFFICULTY_OPTIONS = [
  { value: 'beginner-friendly', label: 'Beginner-friendly' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const editorNameForApi = (editorName) => (
  editorName === DEFAULT_EDITOR_NAME ? DEFAULT_EDITOR_API_NAME : editorName
);

const renderInlineLinks = (text) => {
  MARKDOWN_LINK_GLOBAL.lastIndex = 0;
  const nodes = [];
  let lastIndex = 0;
  let match;

  while ((match = MARKDOWN_LINK_GLOBAL.exec(text)) !== null) {
    const [fullMatch, label, rawUrl] = match;
    let normalizedUrl = (rawUrl || '').trim().replace(/[.,;:]+$/, '');
    if (/^https?:[^/]/i.test(normalizedUrl)) {
      normalizedUrl = normalizedUrl.replace(/^(https?):/i, '$1://');
    }
    if (!/^https?:\/\//i.test(normalizedUrl)) {
      normalizedUrl = `https://${normalizedUrl}`;
    }
    const start = match.index;

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }

    nodes.push(
      <a key={`${normalizedUrl}-${start}`} href={normalizedUrl} target="_blank" rel="noreferrer noopener">
        {label}
      </a>
    );

    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  const finalNodes = [];
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (typeof node === 'string') {
      const bracketPattern = /\[([^\]]+)\]/g;
      let bracketMatch;
      let bracketLastIndex = 0;
      const bracketNodes = [];

      while ((bracketMatch = bracketPattern.exec(node)) !== null) {
        const [fullBracketMatch, bracketText] = bracketMatch;
        const bracketStart = bracketMatch.index;

        if (bracketStart > bracketLastIndex) {
          bracketNodes.push(node.slice(bracketLastIndex, bracketStart));
        }

        bracketNodes.push(
          <span key={`ref-${i}-${bracketStart}`} className="web-reference">
            [{bracketText}]
          </span>
        );

        bracketLastIndex = bracketStart + fullBracketMatch.length;
      }

      if (bracketLastIndex < node.length) {
        bracketNodes.push(node.slice(bracketLastIndex));
      }

      if (bracketNodes.length > 0) {
        finalNodes.push(...bracketNodes);
      } else {
        finalNodes.push(node);
      }
    } else {
      finalNodes.push(node);
    }
  }

  const parseBold = (nodesList) => {
    const nextNodes = [];
    for (let j = 0; j < nodesList.length; j++) {
      const n = nodesList[j];
      if (typeof n === 'string') {
        const boldPattern = /\*\*([^*]+)\*\*/g;
        let boldMatch;
        let boldLastIndex = 0;
        const boldNodes = [];
        while ((boldMatch = boldPattern.exec(n)) !== null) {
          if (boldMatch.index > boldLastIndex) {
            boldNodes.push(n.slice(boldLastIndex, boldMatch.index));
          }
          boldNodes.push(<strong key={`bold-${j}-${boldMatch.index}`}>{boldMatch[1]}</strong>);
          boldLastIndex = boldMatch.index + boldMatch[0].length;
        }
        if (boldLastIndex < n.length) {
          boldNodes.push(n.slice(boldLastIndex));
        }
        nextNodes.push(...(boldNodes.length ? boldNodes : [n]));
      } else {
        nextNodes.push(n);
      }
    }
    return nextNodes;
  };

  const currentNodes = finalNodes.length ? finalNodes : [text];
  return parseBold(currentNodes);
};

const renderArticleLine = (line, index, showLoadingDots = false) => {
  const trimmed = line.trim();
  if (!trimmed) {
    if (showLoadingDots) {
      return <p key={index}><span className="loading-dots">Generating</span></p>;
    }
    return <div key={index} className="article-spacer" />;
  }

  const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
  if (headingMatch) {
    const level = headingMatch[1].length;
    const contentText = headingMatch[2];
    const Tag = `h${level}`;
    return (
      <Tag key={index} className={`article-heading-${level}`}>
        {renderInlineLinks(contentText)}
        {showLoadingDots && <span className="loading-dots">Generating</span>}
      </Tag>
    );
  }

  return (
    <p key={index}>
      {renderInlineLinks(line)}
      {showLoadingDots && <span className="loading-dots">Generating</span>}
    </p>
  );
};

const getSmeAvatarColor = (type) => {
  const map = {
    'Business': { bg: '#ffedd5', color: '#ea580c', letter: 'B' },
    'Technology': { bg: '#f3e8ff', color: '#9333ea', letter: 'EC' },
    'Law': { bg: '#e0f2fe', color: '#0284c7', letter: 'AR' },
    'Finance': { bg: '#dcfce7', color: '#16a34a', letter: 'F' },
  };
  return map[type] || { bg: '#f1f5f9', color: '#475569', letter: type ? type.slice(0, 2).toUpperCase() : 'SM' };
};

// Custom standard dropdown selector
const StudioDropdown = ({ icon: Icon, value, onChange, options, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selectedOption = options.find(o => o.value === value);

  return (
    <div className={`studio-custom-select ${disabled ? 'disabled' : ''}`} ref={containerRef}>
      <div 
        className={`studio-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="studio-select-trigger-content">
          <Icon size={16} strokeWidth={2} className="studio-field-icon" />
          <span className="studio-select-value">
            {selectedOption ? selectedOption.label : ''}
          </span>
        </div>
        <ChevronDown size={16} className="studio-chevron" />
      </div>

      {isOpen && (
        <div className="studio-options-dropdown">
          {options.map((opt) => (
            <div 
              key={opt.value}
              className={`studio-option ${value === opt.value ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              <span>{opt.label}</span>
              {value === opt.value && <Check size={14} className="studio-check" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ArticleGenerator = () => {
  const [activeTab, setActiveTab] = useState('generate');
  const [isGeneratingView, setIsGeneratingView] = useState(false);
  const [smes, setSmes] = useState([]);
  const [editors, setEditors] = useState([]);
  const [selectedSmes, setSelectedSmes] = useState([]);
  const [selectedEditor, setSelectedEditor] = useState(DEFAULT_EDITOR_NAME);
  const [articleLength, setArticleLength] = useState('long');
  const [englishStyle, setEnglishStyle] = useState('us');
  const [englishDifficulty, setEnglishDifficulty] = useState('intermediate');
  const [enableWebSearch, setEnableWebSearch] = useState(false);

  const [topic, setTopic] = useState('');
  const [article, setArticle] = useState('');
  const [evidenceNotes, setEvidenceNotes] = useState([]);
  const [titleChoices, setTitleChoices] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [regenerationIndex, setRegenerationIndex] = useState(0);
  const [streamStatus, setStreamStatus] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [publishingToEncye, setPublishingToEncye] = useState(false);
  const [encyeArticleId, setEncyeArticleId] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);

  const [basePrompt, setBasePrompt] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState(null);

  // SME Dropdown Search
  const [isSmeOpen, setIsSmeOpen] = useState(false);
  const [smeSearch, setSmeSearch] = useState('');
  const smeContainerRef = useRef(null);

  // Editor Dropdown Search
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const editorContainerRef = useRef(null);

  // Toggle "How it works"
  const [showHowItWorks, setShowHowItWorks] = useState(true);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setSimulatedProgress(prev => {
          if (prev < 20) return prev + 2;
          if (prev < 50) return prev + 1;
          if (prev < 85) return prev + 0.5;
          if (prev < 95) return prev + 0.2;
          return prev;
        });
      }, 800);
    } else {
      setSimulatedProgress(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (smeContainerRef.current && !smeContainerRef.current.contains(e.target)) {
        setIsSmeOpen(false);
      }
      if (editorContainerRef.current && !editorContainerRef.current.contains(e.target)) {
        setIsEditorOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const fetchData = async () => {
    try {
      setFetching(true);
      const [smesResponse, editorsResponse] = await Promise.all([
        smeService.getAll(),
        editorService.getAll()
      ]);
      const activeSmes = (smesResponse.data || []).filter(sme => sme.isActive !== false);
      setSmes(activeSmes);
      const activeEditors = (editorsResponse.data || []).filter(editor => editor.isActive !== false);
      setEditors(activeEditors);
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load article configuration.'));
      console.error('Error fetching data:', err);
    } finally {
      setFetching(false);
    }
  };

  const fetchBasePrompt = useCallback(async () => {
    try {
      setPromptLoading(true);
      setPromptError(null);
      const response = await articleService.getBaseSystemPrompt({
        smes: selectedSmes,
        editor: selectedEditor,
        article_length: articleLength,
        english_style: englishStyle,
        english_difficulty: englishDifficulty,
        topic,
        regenerate: false,
        previous_article: null,
        regeneration_index: regenerationIndex,
      });
      setBasePrompt(response?.data?.prompt || '');
    } catch (err) {
      setPromptError('Failed to load base system prompt.');
      console.error('Error loading base prompt:', err);
    } finally {
      setPromptLoading(false);
    }
  }, [selectedSmes, selectedEditor, articleLength, englishStyle, englishDifficulty, topic, regenerationIndex]);

  useEffect(() => {
    if (activeTab !== 'prompt') {
      return;
    }
    fetchBasePrompt();
  }, [activeTab, fetchBasePrompt]);

  const handleSmeToggle = (smeName) => {
    setSelectedSmes((prev) => {
      if (prev.includes(smeName)) {
        return prev.filter((name) => name !== smeName);
      }
      return [...prev, smeName];
    });
  };

  const startGeneration = async ({ regenerate = false } = {}) => {
    setLoading(true);
    setIsGeneratingView(true);
    setError(null);
    const previousArticle = regenerate ? article : null;
    if (!regenerate) {
      setRegenerationIndex(0);
    }
    setArticle('');
    setEncyeArticleId('');
    setEvidenceNotes([]);
    setTitleChoices([]);
    setSelectedTitle('');
    setStreamStatus(regenerate ? 'Regenerating with a different style and structure...' : 'Starting generation...');

    if (!topic.trim()) {
      setError('Please enter a topic');
      setLoading(false);
      return;
    }

    if (selectedSmes.length === 0) {
      setError('Please select at least one SME');
      setLoading(false);
      return;
    }

    try {
      const selectedSmeIds = smes
        .filter((sme) => selectedSmes.includes(sme.name))
        .map((sme) => sme._id)
        .filter(Boolean);
      Promise.all(selectedSmeIds.map((id) => smeService.markRecent(id))).catch((recentError) => {
        console.error('Failed to update recently used SMEs:', recentError);
      });

      const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://127.0.0.1:5001';
      const response = await fetch(`${API_BASE}/api/articles/generate-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          topic,
          smes: selectedSmes,
          editor: editorNameForApi(selectedEditor),
          article_length: articleLength,
          english_style: englishStyle,
          english_difficulty: englishDifficulty,
          regenerate,
          previous_article: previousArticle,
          regeneration_index: regenerate ? regenerationIndex + 1 : 0,
          enable_web_search: enableWebSearch,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          expireStoredSession();
          return;
        }
        const errorMessage = await parseFetchErrorMessage(response, `Server Error (${response.status}).`);
        throw new Error(errorMessage);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        streamBuffer += decoder.decode(value, { stream: true });
        const lines = streamBuffer.split('\n');
        streamBuffer = lines.pop() || '';

        for (const line of lines) {
          const normalizedLine = line.replace(/\r$/, '');
          if (!normalizedLine.startsWith('data: ')) continue;

          try {
            const data = JSON.parse(normalizedLine.slice(6));

            if (data.type === 'status') {
              setStreamStatus((prev) => (prev ? `${prev}\n${data.content}` : data.content));
            } else if (data.type === 'content') {
              setArticle((prev) => prev + data.content);
            } else if (data.type === 'content_final') {
              setArticle(data.content || '');
            } else if (data.type === 'title_choices') {
              const choices = Array.isArray(data.content) ? data.content : [];
              setTitleChoices(choices);
              if (choices.length > 0) {
                setSelectedTitle(choices[0]);
              }
            } else if (data.type === 'evidence_notes') {
              setEvidenceNotes(Array.isArray(data.content) ? data.content : []);
            } else if (data.type === 'error') {
              setError(data.content || 'Generation failed.');
              setLoading(false);
              setStreamStatus('');
              return;
            } else if (data.type === 'done') {
              setStreamStatus('');
              if (regenerate) {
                setRegenerationIndex((prev) => prev + 1);
              }
              setLoading(false);
            }
          } catch (streamError) {
            console.error('Error parsing stream data:', streamError);
          }
        }
      }

      streamBuffer += decoder.decode();
      const finalLine = streamBuffer.replace(/\r$/, '');
      if (finalLine.startsWith('data: ')) {
        try {
          const data = JSON.parse(finalLine.slice(6));
          if (data.type === 'content_final') {
            setArticle(data.content || '');
          } else if (data.type === 'title_choices') {
            const choices = Array.isArray(data.content) ? data.content : [];
            setTitleChoices(choices);
            if (choices.length > 0) {
              setSelectedTitle(choices[0]);
            }
          } else if (data.type === 'evidence_notes') {
            setEvidenceNotes(Array.isArray(data.content) ? data.content : []);
          } else if (data.type === 'done') {
            setStreamStatus('');
            if (regenerate) {
              setRegenerationIndex((prev) => prev + 1);
            }
            setLoading(false);
          }
        } catch (streamError) {
          console.error('Error parsing final stream data:', streamError);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to generate article');
      setLoading(false);
    }
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    startGeneration({ regenerate: false });
  };

  const handleRegenerate = () => {
    startGeneration({ regenerate: true });
  };

  const handlePublishToEncye = async () => {
    if (!article || publishingToEncye || encyeArticleId) return;
    try {
      setPublishingToEncye(true);
      setError(null);
      const response = await articleService.publishToEncye({
        title: selectedTitle || topic,
        content: article,
      });
      setEncyeArticleId(String(response.data?.encye?.data?._id || 'published'));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to publish article to Encye.');
    } finally {
      setPublishingToEncye(false);
    }
  };

  const publishButtonLabel = encyeArticleId
    ? 'Published to Encye'
    : publishingToEncye
      ? 'Publishing...'
      : 'Publish to Encye';

  const handleClearForm = () => {
    setTopic('');
    setSelectedSmes([]);
    setSelectedEditor(DEFAULT_EDITOR_NAME);
    setArticleLength('long');
    setEnglishStyle('us');
    setEnglishDifficulty('intermediate');
    setEnableWebSearch(false);
    setArticle('');
    setEvidenceNotes([]);
    setTitleChoices([]);
    setError(null);
  };

  const handleCopyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (copyError) {
      console.error('Copy failed', copyError);
    }
  };

  const expectedWords = articleLength === 'short' ? 450 : 800;
  const currentWords = article ? article.split(/\s+/).filter(Boolean).length : 0;
  
  let progressPercent = 100;
  if (loading) {
    if (currentWords === 0) {
      const statusCount = streamStatus ? streamStatus.split('\n').filter(Boolean).length : 0;
      const statusProgress = Math.min(30, statusCount * 5); 
      progressPercent = Math.max(statusProgress, Math.floor(simulatedProgress));
    } else {
      const wordProgress = Math.floor((currentWords / expectedWords) * 70);
      progressPercent = Math.min(99, Math.max(30 + wordProgress, Math.floor(simulatedProgress)));
    }
  }

  const renderTitleChoicePicker = (compact = false) => {
    const choices = titleChoices.length ? titleChoices : (selectedTitle ? [selectedTitle] : []);
    if (!choices.length) {
      return loading ? (
        <div className={`studio-title-choice-panel ${compact ? 'compact' : ''}`}>
          <div className="studio-title-choice-head">
            <span>AI title options</span>
            <small>Generating 4 choices...</small>
          </div>
          <div className="studio-title-choice-loading">
            <span className="loading-dots">Generating</span>
          </div>
        </div>
      ) : null;
    }

    return (
      <div className={`studio-title-choice-panel ${compact ? 'compact' : ''}`}>
        <div className="studio-title-choice-head">
          <span>Choose article title</span>
          <small>AI generated 4 options for this article.</small>
        </div>
        <div className="studio-title-choice-grid">
          {choices.map((choice, index) => {
            const active = selectedTitle === choice;
            return (
              <button
                type="button"
                key={`${choice}-${index}`}
                className={`studio-title-choice-card ${active ? 'active' : ''}`}
                onClick={() => setSelectedTitle(choice)}
                disabled={loading && !titleChoices.length}
              >
                <span className="studio-title-choice-index">{index + 1}</span>
                <span className="studio-title-choice-text">{choice}</span>
                {active && <Check size={15} className="studio-title-choice-check" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (fetching) {
    return (
      <div className="app-loading-container">
        <div className="app-loading-spinner" />
        <p>Loading SMEs and Editors...</p>
      </div>
    );
  }

  const filteredSmes = smes.filter(sme =>
    sme.name.toLowerCase().includes(smeSearch.toLowerCase()) ||
    sme.type.toLowerCase().includes(smeSearch.toLowerCase())
  );

  return (
    <div className="studio-page">
      <div className="studio-main">
        {/* Top Header Grid */}
        <div className="studio-header">
          <div className="studio-header-left">
            <div className="studio-icon-wrapper">
              <Edit3 size={24} className="studio-banner-icon" />
            </div>
            <div>
              <h1>Content Studio</h1>
              <p>Generate collaborative long-form articles and inspect the base system prompt.</p>
            </div>
          </div>
          <div className="studio-header-right">
            <button 
              className={`studio-header-btn ${activeTab === 'prompt' ? 'active' : ''}`}
              onClick={() => setActiveTab(activeTab === 'prompt' ? 'generate' : 'prompt')}
            >
              View Base System Prompt
            </button>
          </div>
        </div>

        {activeTab === 'prompt' ? (
          <div className="studio-card prompt-card-tab">
            <div className="studio-prompt-header">
              <h2>Base System Prompt</h2>
              <span className="studio-prompt-badge">Read Only</span>
            </div>
            <p className="studio-prompt-desc">
              This is the default prompt template loaded from the backend before any editor-level instructions are applied.
            </p>
            {promptLoading && <div className="studio-spinner-wrap"><div className="app-loading-spinner" /></div>}
            {promptError && <div className="studio-error">{promptError}</div>}
            {!promptLoading && !promptError && (
              <textarea
                className="studio-prompt-textarea"
                value={basePrompt}
                readOnly
              />
            )}
          </div>
        ) : isGeneratingView ? (
          <div className="studio-generating-layout">
            <div className="studio-readonly-sidebar">
              <div className="studio-config-title">
                <Sparkles size={16} className="studio-title-icon-sparkles" />
                <h2>Article Configuration</h2>
                <ChevronUp size={16} className="studio-collapse-icon" />
              </div>
              
              <div className="readonly-scroll-content">
                <div className="readonly-group">
                  <span className="readonly-label">Article Topic</span>
                  <span className="readonly-value">{topic}</span>
                </div>
                <div className="readonly-group">
                  <span className="readonly-label">Article Length</span>
                  <span className="readonly-value">{ARTICLE_LENGTH_OPTIONS.find(o => o.value === articleLength)?.label || articleLength}</span>
                </div>
                <div className="readonly-group">
                  <span className="readonly-label">English Style</span>
                  <span className="readonly-value">{ENGLISH_STYLE_OPTIONS.find(o => o.value === englishStyle)?.label || englishStyle}</span>
                </div>
                <div className="readonly-group">
                  <span className="readonly-label">English Difficulty Level</span>
                  <span className="readonly-value">{ENGLISH_DIFFICULTY_OPTIONS.find(o => o.value === englishDifficulty)?.label || englishDifficulty}</span>
                </div>
                <div className="readonly-group">
                  <span className="readonly-label">Web Search</span>
                  <span className={`readonly-badge ${enableWebSearch ? 'enabled' : 'disabled'}`}>
                    {enableWebSearch && <Check size={12} strokeWidth={3} className="readonly-badge-icon" />}
                    {enableWebSearch ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="readonly-group">
                  <span className="readonly-label">Selected SMEs</span>
                  <span className="readonly-selected-count">{selectedSmes.length} selected</span>
                  <div className="readonly-sme-list">
                    {selectedSmes.map(smeName => {
                      const sme = smes.find(s => s.name === smeName);
                      const colors = getSmeAvatarColor(sme ? sme.type : '');
                      return (
                        <div className="sme-mini-card" key={smeName}>
                          <div className="sme-mini-avatar" style={{ background: colors.bg, color: colors.color }}>{colors.letter}</div>
                          <div className="sme-mini-info">
                            <span className="sme-mini-name">{smeName}</span>
                            <span className="sme-mini-role">{sme ? sme.type : ''}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="readonly-group">
                  <span className="readonly-label">Editor</span>
                  <div className="editor-mini-card">
                    <span className="editor-mini-name">{selectedEditor}</span>
                  </div>
                </div>
              </div>
              
              <div className="readonly-actions-footer">
                <button 
                  className="studio-btn-edit-config"
                  onClick={() => setIsGeneratingView(false)}
                  disabled={loading}
                >
                  <PenTool size={16} /> Edit Configuration
                </button>
              </div>
            </div>

            <div className="studio-streaming-panel">
              <div className="streaming-header">
                <div className="streaming-title">
                  <Sparkles size={20} className="streaming-icon" />
                  <h2>{streamStatus ? streamStatus.split('\n').filter(Boolean).pop() : "Generated Article"}</h2>
                </div>
                <div className="streaming-actions">
                  {loading && (
                    <div className="streaming-badge">
                      <div className="streaming-dot"></div>
                      <span>Streaming</span>
                    </div>
                  )}
                  <button 
                    className="streaming-action-btn regenerate-btn" 
                    onClick={handleRegenerate}
                    disabled={loading || !topic.trim() || selectedSmes.length === 0}
                    style={{ color: '#0284c7', fontWeight: 600 }}
                  >
                    Regenerate
                  </button>
                  {article && (
                    <button
                      className="streaming-action-btn studio-publish-btn"
                      onClick={handlePublishToEncye}
                      disabled={loading || publishingToEncye || Boolean(encyeArticleId)}
                    >
                      <Ship size={14}/> {publishButtonLabel}
                    </button>
                  )}
                  <button
                    className="streaming-action-btn"
                    onClick={() => handleCopyText(selectedTitle ? `${selectedTitle}\n\n${article}` : article)}
                  >
                    <Copy size={14}/> {isCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="streaming-body">
                {renderTitleChoicePicker(true)}
                <div className="article-title-gradient">
                  <h1>{selectedTitle || 'Preparing title options...'}</h1>
                  <div className="gradient-line"></div>
                </div>
                
                <div className="studio-article-body">
                  {article 
                    ? article.split('\n').map((line, index, arr) => {
                        const isLastLine = index === arr.length - 1;
                        return renderArticleLine(line, index, isLastLine && loading);
                      })
                    : (loading ? <p><span className="loading-dots">Generating</span></p> : null)
                  }
                </div>
                
                {evidenceNotes.length > 0 && (
                  <div className="studio-evidence-wrap">
                    <h3>Evidence Notes</h3>
                    <div className="studio-evidence-list">
                      {evidenceNotes.map((note, index) => (
                        <div className="studio-evidence-card" key={`${note.source_id || 'S'}-${index}`}>
                          <div className="studio-evidence-header">
                            <span className="evidence-badge">{note.source_id || `S${index + 1}`}</span>
                            <span className="evidence-outlet">{note.outlet || 'Unknown outlet'}</span>
                          </div>
                          <h4 className="evidence-title">{note.title || 'Untitled source'}</h4>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="progress-footer">
                <div className="progress-footer-top">
                  <div className="progress-text">
                    <Sparkles size={16} className="studio-title-icon-sparkles" />
                    {loading 
                      ? `Generating... ${currentWords} words so far`
                      : `Generation Complete — ${currentWords} words`}
                  </div>
                  <div className="progress-bar-container inline">
                    <div className="progress-bar-fill green" style={{ width: `${progressPercent}%` }}></div>
                  </div>
                  <div className="progress-percentage">
                    {progressPercent}%
                  </div>
                </div>
                
                {loading && (
                  <div className="progress-hint-wrapper">
                    {/* <Lightbulb size={14} className="progress-hint-icon" /> */}
                    {/* <span className="progress-hint">
                      You can pause or stop generation anytime. Your progress is saved automatically.
                    </span> */}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="studio-content-layout">
            <div className="studio-card">
              <div className="studio-config-title">
                <Layout size={16} className="studio-title-icon" />
                <h2>Article Configuration</h2>
              </div>

              {error && <div className="studio-error">{error}</div>}

              <form onSubmit={handleGenerate} className="studio-form">
                
                {/* Topic */}
                <div className="studio-form-group">
                  <label>Article Topic <span className="req">*</span></label>
                  <div className="studio-input-wrapper">
                    <input
                      type="text"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder="Enter the topic for your article, or add &quot;in 300 words&quot;"
                      required
                      disabled={loading}
                      className="studio-input"
                    />
                  </div>
                  <p className="studio-hint">
                    You can define the topic, for example: &quot;AI safety in today's world&quot;.
                  </p>
                </div>

                {/* Length, Style, Difficulty Grid */}
                <div className="studio-fields-grid">
                  <div className="studio-form-group">
                    <label>Article Length <span className="req">*</span></label>
                    <StudioDropdown 
                      icon={FileText} 
                      value={articleLength} 
                      onChange={setArticleLength} 
                      options={ARTICLE_LENGTH_OPTIONS}
                      disabled={loading}
                    />
                  </div>
                  <div className="studio-form-group">
                    <label>English Style <span className="req">*</span></label>
                    <StudioDropdown 
                      icon={BookOpen} 
                      value={englishStyle} 
                      onChange={setEnglishStyle} 
                      options={ENGLISH_STYLE_OPTIONS}
                      disabled={loading}
                    />
                  </div>
                  <div className="studio-form-group">
                    <label>English Difficulty Level <span className="req">*</span></label>
                    <StudioDropdown 
                      icon={Clock} 
                      value={englishDifficulty} 
                      onChange={setEnglishDifficulty} 
                      options={ENGLISH_DIFFICULTY_OPTIONS}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Web Search */}
                <div className="studio-form-group">
                  <label className="studio-checkbox-label">
                    <div className="studio-checkbox-wrap">
                      <input
                        type="checkbox"
                        checked={enableWebSearch}
                        onChange={(e) => setEnableWebSearch(e.target.checked)}
                        disabled={loading}
                      />
                      <div className="studio-checkbox-custom">
                        {enableWebSearch && <Check size={12} strokeWidth={3} />}
                      </div>
                    </div>
                    <span>Enable web search for this generation</span>
                  </label>
                  <p className="studio-checkbox-hint">Web search will use materials from the web for making the article.</p>
                </div>

                {/* Dual Select Column (SMEs + Editor) */}
                <div className="studio-dual-select">
                  
                  {/* Select SMEs */}
                  <div className="studio-select-panel" ref={smeContainerRef}>
                    <label>Select SMEs <span className="req">*</span></label>
                    
                    <div 
                      className={`studio-dropdown-trigger ${isSmeOpen ? 'open' : ''}`}
                      onClick={() => !loading && setIsSmeOpen(!isSmeOpen)}
                    >
                      <span>
                        {selectedSmes.length > 0 
                          ? `${selectedSmes.length} selected` 
                          : 'Select SMEs'}
                      </span>
                      <ChevronDown size={18} className="studio-chevron" />
                    </div>

                    {isSmeOpen && (
                      <div className="studio-select-dropdown">
                        <div className="studio-dropdown-search">
                          <Search size={16} />
                          <input 
                            type="text" 
                            placeholder="Search SMEs..." 
                            value={smeSearch}
                            onChange={(e) => setSmeSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        
                        <div className="studio-dropdown-list">
                          {filteredSmes.map((sme) => {
                            const colors = getSmeAvatarColor(sme.type);
                            const isChecked = selectedSmes.includes(sme.name);
                            return (
                              <div 
                                key={sme._id}
                                className={`studio-list-item ${isChecked ? 'active' : ''}`}
                                onClick={() => handleSmeToggle(sme.name)}
                              >
                                <div className="studio-item-checkbox">
                                  {isChecked && <Check size={12} strokeWidth={3} />}
                                </div>
                                <div 
                                  className="studio-item-avatar" 
                                  style={{ background: colors.bg, color: colors.color }}
                                >
                                  {colors.letter}
                                </div>
                                <div className="studio-item-info">
                                  <span className="studio-item-name">{sme.name}</span>
                                  <span className="studio-item-sub">{sme.type}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="studio-dropdown-footer">
                          <span>{selectedSmes.length} of {smes.length} selected</span>
                          <button 
                            type="button" 
                            className="studio-btn-clear" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSmes([]);
                            }}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Select Editor */}
                  <div className="studio-select-panel" ref={editorContainerRef}>
                    <label>Select Editor</label>

                    <div 
                      className={`studio-dropdown-trigger ${isEditorOpen ? 'open' : ''}`}
                      onClick={() => !loading && setIsEditorOpen(!isEditorOpen)}
                    >
                      <span>{selectedEditor}</span>
                      <ChevronDown size={18} className="studio-chevron" />
                    </div>

                    {isEditorOpen && (
                      <div className="studio-select-dropdown">
                        <div className="studio-dropdown-list editor-list">
                          {/* Default Editor option */}
                          <div 
                            className={`studio-list-item radio ${selectedEditor === DEFAULT_EDITOR_NAME ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedEditor(DEFAULT_EDITOR_NAME);
                              setIsEditorOpen(false);
                            }}
                          >
                            <div className="studio-item-radio">
                              {selectedEditor === DEFAULT_EDITOR_NAME && <div className="radio-inner" />}
                            </div>
                            <div className="studio-item-info">
                              <span className="studio-item-name">{DEFAULT_EDITOR_NAME}</span>
                              <span className="studio-item-sub">No explicit attribution phrasing</span>
                            </div>
                          </div>

                          {/* Custom Editors */}
                          {editors.filter(ed => ed.name !== 'Encye Editor').map((editor) => {
                            const isChecked = selectedEditor === editor.name;
                            return (
                              <div 
                                key={editor._id}
                                className={`studio-list-item radio ${isChecked ? 'active' : ''}`}
                                onClick={() => {
                                  setSelectedEditor(editor.name);
                                  setIsEditorOpen(false);
                                }}
                              >
                                <div className="studio-item-radio">
                                  {isChecked && <div className="radio-inner" />}
                                </div>
                                <div className="studio-item-info">
                                  <span className="studio-item-name">{editor.name}</span>
                                  <span className="studio-item-sub">{editor.style}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                </div>

                {/* How it works */}
                <div className="studio-how-it-works">
                  <div 
                    className="studio-how-trigger"
                    onClick={() => setShowHowItWorks(!showHowItWorks)}
                  >
                    <div className="how-trigger-label">
                      <Info size={16} />
                      <span>How it works</span>
                    </div>
                    <ChevronDown size={16} className={`how-chevron ${showHowItWorks ? 'open' : ''}`} />
                  </div>

                  {showHowItWorks && (
                    <div className="studio-how-body">
                      <p>Our AI will collaborate with the selected SMEs and editor to generate a comprehensive article.</p>
                      
                      <div className="studio-how-steps">
                        <div className="how-step">
                          <div className="how-step-icon">
                            <FileText size={18} />
                          </div>
                          <div className="how-step-text">
                            <h3>1. Analyze Topic</h3>
                            <p>AI understands your topic and prepares the context.</p>
                          </div>
                        </div>

                        <div className="how-step">
                          <div className="how-step-icon">
                            <BookOpen size={18} />
                          </div>
                          <div className="how-step-text">
                            <h3>2. SME Collaboration</h3>
                            <p>Selected SMEs contribute their expertise and perspectives.</p>
                          </div>
                        </div>

                        <div className="how-step">
                          <div className="how-step-icon">
                            <Sparkles size={18} />
                          </div>
                          <div className="how-step-text">
                            <h3>3. Editor Refinement</h3>
                            <p>Editor reviews and refines the content for quality and style.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Stream Status Status Output */}
                {streamStatus && (
                  <div className="studio-status-box">
                    {streamStatus.split('\n').filter(Boolean).map((line, i) => (
                      <div key={i} className="studio-status-line">
                        <ArrowRight size={12} />
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions Footer */}
                <div className="studio-actions-footer">
                  <button 
                    type="button" 
                    className="studio-btn-reset" 
                    onClick={handleClearForm}
                    disabled={loading}
                  >
                    Clear
                  </button>
                  <button 
                    type="submit" 
                    className="studio-btn-generate"
                    disabled={loading || selectedSmes.length === 0 || !topic.trim()}
                  >
                    {loading ? 'Generating...' : 'Generate Article'}
                    <Sparkles size={16} strokeWidth={2.5} />
                  </button>
                </div>

              </form>
            </div>

            {/* Generated Output Area (Optional RHS card or bottom card depending on layout/width) */}
            {article && (
              <div className="studio-card output-card">
                <div className="studio-output-header">
                  <h2>Generated Article</h2>
                  <div className="studio-output-actions">
                    <button
                      className="studio-header-btn studio-publish-btn"
                      onClick={handlePublishToEncye}
                      disabled={loading || publishingToEncye || Boolean(encyeArticleId)}
                    >
                      <Ship size={15} />
                      {publishButtonLabel}
                    </button>
                    <button 
                      className="studio-header-btn font-medium"
                      onClick={() => {
                        const textToCopy = selectedTitle ? `${selectedTitle}\n\n${article}` : article;
                        handleCopyText(textToCopy);
                      }}
                      disabled={loading}
                    >
                      {isCopied ? 'Copied!' : 'Copy'}
                    </button>
                    <button 
                      className="studio-btn-generate text-sm-btn"
                      onClick={handleRegenerate}
                      disabled={loading || !topic.trim() || selectedSmes.length === 0}
                    >
                      {loading ? 'Regenerating...' : 'Regenerate'}
                    </button>
                  </div>
                </div>

                {renderTitleChoicePicker()}

                <div className="studio-article-body">
                  {article.split('\n').map((line, index) => renderArticleLine(line, index))}
                </div>

                {/* Evidence Notes */}
                {evidenceNotes.length > 0 && (
                  <div className="studio-evidence-wrap">
                    <h3>Evidence Notes</h3>
                    <p className="studio-hint text-xs margin-b">
                      Web-search references are shown as plain-language notes for transparency. URLs are intentionally hidden.
                    </p>
                    <div className="studio-evidence-list">
                      {evidenceNotes.map((note, index) => (
                        <div className="studio-evidence-card" key={`${note.source_id || 'S'}-${index}`}>
                          <div className="studio-evidence-header">
                            <span className="evidence-badge">{note.source_id || `S${index + 1}`}</span>
                            <span className="evidence-outlet">{note.outlet || 'Unknown outlet'}</span>
                          </div>
                          <h4 className="evidence-title">{note.title || 'Untitled source'}</h4>
                          {note.relevance_note && <p className="evidence-meta">{note.relevance_note}</p>}
                          {note.summary && <p className="evidence-summary">{note.summary}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleGenerator;
